// Where a promoted card lands.
//
// Not the creator page: useRankedCreators only gives someone a page once they
// have two clips in the catalog, and a sponsor may have none at all. This page
// works either way — portrait, blurb, their link if they gave one, and their
// clips underneath if they have any.
//
// Nothing leaves the app on the tap that got here. The outbound link is a
// button on this page, sitting next to their clips, so the choice is visible
// rather than sprung on someone who tapped a face.
import { ExternalLink } from 'lucide-react';
import { DetailPage } from './Forum';
import { Face, PlatformGlyph, TechniqueGrid, type LibraryBridge } from './Spotlights';
import { useTechniques } from '../hooks/useTechniques';
import { profileUrl, recordPromotionClick, type LivePromotion } from '../api/promotions';

export default function SponsorPage({ promo, library, onBack }: {
  promo: LivePromotion;
  library?: LibraryBridge;
  onBack: () => void;
}) {
  const { techniques } = useTechniques();
  const theirs = techniques.filter((t) => t.author?.trim() === promo.handle);

  const visit = () => {
    // Counted for the admin's benefit only — the sponsor never sees the
    // number. Fire and forget: a failed count must not block the link.
    recordPromotionClick(promo.uid);
    window.open(promo.url!, '_blank', 'noopener,noreferrer');
  };

  return (
    <DetailPage title={promo.name} onBack={onBack}>
      <div className="sp-scroll">
        <div className="sponsor-head">
          <Face handle={promo.handle} className="sponsor-face" />
          <div className="sponsor-meta">
            <span className="sponsor-name">{promo.name}</span>
            {promo.blurb && <span className="sponsor-blurb">{promo.blurb}</span>}
            <a
              className="sp-handle"
              href={profileUrl(promo.handle, promo.platform)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PlatformGlyph platform={promo.platform} />
              @{promo.handle}
            </a>
          </div>
        </div>

        {promo.url && (
          <button type="button" className="sponsor-visit" onClick={visit}>
            <span>Visit {hostOf(promo.url)}</span>
            <ExternalLink size={16} />
          </button>
        )}

        <div className="sponsor-clips-head">
          Clips in Gnosis · {theirs.length}
        </div>
        {theirs.length > 0 ? (
          <TechniqueGrid list={theirs} library={library} />
        ) : (
          <p className="sp-empty">Nothing here yet.</p>
        )}
      </div>
    </DetailPage>
  );
}

/** The bare domain, so the button says where it goes rather than showing a
 *  full URL that wraps onto three lines on a phone. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
